import { useQuery, UseQueryResult } from '@tanstack/react-query';
import type { UTCTimestamp } from 'lightweight-charts';
import { HlCandleRaw, HlCandleSnapshotPayload, postHyperliquidInfoAt } from '@/api/hlApi';

/** Intervals supported by HyperLiquid `candleSnapshot`. */
export type HlInterval =
    | '1m'
    | '3m'
    | '5m'
    | '15m'
    | '30m'
    | '1h'
    | '2h'
    | '4h'
    | '8h'
    | '12h'
    | '1d'
    | '3d'
    | '1w'
    | '1M';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

export const HL_INTERVAL_MS: Record<HlInterval, number> = {
    '1m': MINUTE_MS,
    '3m': 3 * MINUTE_MS,
    '5m': 5 * MINUTE_MS,
    '15m': 15 * MINUTE_MS,
    '30m': 30 * MINUTE_MS,
    '1h': HOUR_MS,
    '2h': 2 * HOUR_MS,
    '4h': 4 * HOUR_MS,
    '8h': 8 * HOUR_MS,
    '12h': 12 * HOUR_MS,
    '1d': DAY_MS,
    '3d': 3 * DAY_MS,
    '1w': 7 * DAY_MS,
    '1M': 30 * DAY_MS,
};

export interface HlCandleBar {
    /** Candle open time, seconds. */
    time: UTCTimestamp;
    /** Candle close time, seconds. */
    closeTime: UTCTimestamp;
    open: number;
    high: number;
    low: number;
    close: number;
}

/** Fixed window (`endTime` inclusive, defaults to now) or the last N candles up to now. */
export type HlCandlesRange = { startTime: number; endTime?: number } | { lastCandles: number };

interface UseHlCandlesParams {
    /** Hyperliquid `/info` URL of the network to read from. */
    infoUrl: string;
    coin: string;
    interval: HlInterval;
    range: HlCandlesRange;
    refetchIntervalMs?: number | false;
    staleTimeMs?: number;
    enabled?: boolean;
}

const mapCandle = (raw: HlCandleRaw): HlCandleBar => ({
    time: Math.floor(raw.t / 1000) as UTCTimestamp,
    closeTime: Math.floor(raw.T / 1000) as UTCTimestamp,
    open: parseFloat(raw.o),
    high: parseFloat(raw.h),
    low: parseFloat(raw.l),
    close: parseFloat(raw.c),
});

const normalizeCandles = (raw: HlCandleRaw[]): HlCandleBar[] => {
    const byTime = new Map<number, HlCandleBar>();
    raw.forEach((item) => {
        const bar = mapCandle(item);
        byTime.set(bar.time, bar);
    });
    return Array.from(byTime.values()).sort((a, b) => a.time - b.time);
};

/** HyperLiquid candles for any interval and window, either from a fixed start or the last N candles. */
export const useHlCandles = ({
    infoUrl,
    coin,
    interval,
    range,
    refetchIntervalMs = false,
    staleTimeMs,
    enabled = true,
}: UseHlCandlesParams): UseQueryResult<HlCandleBar[], Error> => {
    const rangeKey =
        'startTime' in range
            ? `${range.startTime}:${range.endTime ?? 'now'}`
            : `last:${range.lastCandles}`;

    return useQuery({
        queryKey: ['hlCandles', infoUrl, coin, interval, rangeKey],
        queryFn: async ({ signal }) => {
            const now = Date.now();
            const startTime =
                'startTime' in range
                    ? range.startTime
                    : now - range.lastCandles * HL_INTERVAL_MS[interval];

            const endTime = 'startTime' in range && range.endTime ? range.endTime : now;
            const response = await postHyperliquidInfoAt<HlCandleRaw[]>(
                infoUrl,
                {
                    type: 'candleSnapshot',
                    req: { coin, interval, startTime, endTime } satisfies HlCandleSnapshotPayload,
                },
                signal,
            );

            return normalizeCandles(response.data ?? []);
        },
        enabled: enabled && !!coin,
        refetchInterval: refetchIntervalMs,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        staleTime: staleTimeMs,
    });
};
